import React, { useReducer, useState, useEffect } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import { Order, OrderProduct } from "../../../models";
import { IOrder, IRouteItem, Timestamp } from "../../../types";
import { OrderStatus } from "../../../types/order/IOrder";
import { HiInformationCircle } from "react-icons/hi";
import {
  submitPublicOrder,
  authenticatePublicAccess,
  PublicAuthParams,
  fetchPublicProducts,
  PublicProduct,
} from "../../../api/publicOrder";
import PublicOrderSuccessPage from "./components/PublicOrderSuccessPage";
import PublicFlowSelection, {
  OrderFlow,
} from "./components/PublicFlowSelection";
import PublicOrderForm from "./components/PublicOrderForm";
import PublicFormConfig from "../../../models/PublicFormConfig";
// Holds id of default pickup location when defined in form config.
let defaultPickupLocationIdGlobal: string | null = null;

// Action types for the reducer
const actionTypes = {
  UPDATE_CUSTOMER: "UPDATE_CUSTOMER",
  ADD_ROUTE: "ADD_ROUTE",
  UPDATE_ROUTE: "UPDATE_ROUTE",
  REMOVE_ROUTE: "REMOVE_ROUTE",
  ADD_CARGO: "ADD_CARGO",
  UPDATE_CARGO: "UPDATE_CARGO",
  REMOVE_CARGO: "REMOVE_CARGO",
  SET_CARGO: "SET_CARGO",
  UPDATE_COMMENT: "UPDATE_COMMENT",
  UPDATE_PROJECT_NUMBER: "UPDATE_PROJECT_NUMBER",
  RESET_ORDER: "RESET_ORDER",
};

// Helper: find earliest delivery stop date and update pickup stops to match
const syncPickupDateWithDeliveries = (route: IRouteItem[]): IRouteItem[] => {
  // Only perform sync when default pickup location is configured
  if (!defaultPickupLocationIdGlobal) return route;

  // Find stops excluding the default pickup itself
  const nonDefaultStops = route.filter(
    (s) =>
      !(s.isPickup && s.location?.id === defaultPickupLocationIdGlobal) &&
      s.stopDate,
  );

  if (nonDefaultStops.length === 0) return route; // Nothing to compare yet

  // Helper to convert various date types to epoch ms
  const toMillis = (date: any): number => {
    if (typeof date === "object" && date !== null) {
      if ("toMillis" in date && typeof date.toMillis === "function")
        return date.toMillis();
      if ("seconds" in date) return (date.seconds as number) * 1000;
    }
    return new Date(date as string).getTime();
  };

  // Determine earliest date among non-default stops
  const earliestDate = nonDefaultStops.reduce((earliest, current) => {
    return toMillis(current.stopDate) < toMillis(earliest)
      ? current.stopDate
      : earliest;
  }, nonDefaultStops[0].stopDate);

  // Update only the default pickup stop date
  return route.map((stop) => {
    if (stop.isPickup && stop.location?.id === defaultPickupLocationIdGlobal) {
      return { ...stop, stopDate: earliestDate };
    }
    return stop;
  });
};

// The reducer function to manage order state
function orderReducer(
  state: IOrder,
  action: { type: string; payload: any },
): IOrder {
  switch (action.type) {
    case actionTypes.UPDATE_CUSTOMER:
      return {
        ...state,
        customer: {
          ...state.customer,
          ...action.payload.value,
        },
      };
    case actionTypes.ADD_ROUTE: {
      const newRoute = [...state.route, action.payload.routeItem];
      // Renumber stops
      for (let i = 0; i < newRoute.length; i++) {
        newRoute[i].stopNumber = i;
      }
      // Ensure pickup stop date matches earliest delivery date
      const syncedRoute = syncPickupDateWithDeliveries(newRoute);
      return { ...state, route: syncedRoute };
    }
    case actionTypes.UPDATE_ROUTE: {
      const newRoute = [...state.route];
      newRoute[action.payload.index] = action.payload.routeItem;
      const syncedRoute = syncPickupDateWithDeliveries(newRoute);
      return { ...state, route: syncedRoute };
    }
    case actionTypes.REMOVE_ROUTE: {
      const newRoute = state.route.filter(
        (_, index) => index !== action.payload.index,
      );
      // Renumber stops
      for (let i = 0; i < newRoute.length; i++) {
        newRoute[i].stopNumber = i;
      }
      const syncedRoute = syncPickupDateWithDeliveries(newRoute);
      return { ...state, route: syncedRoute };
    }
    case actionTypes.ADD_CARGO:
      return {
        ...state,
        cargo: [...state.cargo, action.payload.product],
      };
    case actionTypes.UPDATE_CARGO:
      return {
        ...state,
        cargo: state.cargo.map((item, index) =>
          index === action.payload.index ? action.payload.product : item,
        ),
      };
    case actionTypes.REMOVE_CARGO:
      return {
        ...state,
        cargo: state.cargo.filter((_, index) => index !== action.payload.index),
      };
    case actionTypes.SET_CARGO:
      return {
        ...state,
        cargo: action.payload.cargo,
      };
    case actionTypes.UPDATE_COMMENT:
      return { ...state, comment: action.payload.value };
    case actionTypes.UPDATE_PROJECT_NUMBER:
      return { ...state, projectNumber: action.payload.value };
    case actionTypes.RESET_ORDER:
      return initializeOrder(Order.default());
    default:
      return state;
  }
}

// Initializer function
const initializeOrder = (initialOrder: IOrder): IOrder => {
  const order = new Order(initialOrder).clone();
  order.route = []; // Start with an empty route
  order.cargo = []; // Start with an empty cargo list
  order.status = OrderStatus.Draft;
  return order;
};

interface PublicOrderPageProps {
  onSubmit?: (order: IOrder) => void;
}

const PublicOrderPage: React.FC<PublicOrderPageProps> = ({ onSubmit }) => {
  const { t, i18n } = useTranslation(["order_form"]);
  const { identifier } = useParams(); // Can be slug or workspaceId
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [authParams, setAuthParams] = useState<PublicAuthParams>({});
  const [order, dispatch] = useReducer(
    orderReducer,
    Order.default(),
    initializeOrder,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState<IOrder | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<OrderFlow | null>(null);
  const [config, setConfig] = useState<any>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<string>("en");
  const [isLanguageChanging, setIsLanguageChanging] = useState(false);
  const [languageReady, setLanguageReady] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  // Get URL parameters and authenticate
  useEffect(() => {
    const authenticate = async () => {
      if (!identifier) {
        setError(t("public_form.authentication.missing_params"));
        setIsAuthenticated(false);
        setIsAuthenticating(false);
        return;
      }

      try {
        // Step 1: Fetch config by slug first, then fall back to workspaceId
        setIsLoadingConfig(true);
        let orderFormConfig = await PublicFormConfig.getByUrlSlug(identifier);
        if (!orderFormConfig) {
          orderFormConfig = await PublicFormConfig.getByWorkspaceId(identifier);
        }

        if (!orderFormConfig) {
          throw new Error(t("public_form.errors.no_config_found"));
        }
        setConfig(orderFormConfig);
        // Store default pickup location id for reducer sync
        defaultPickupLocationIdGlobal =
          orderFormConfig.defaultPickupLocation?.id ?? null;
        setIsLoadingConfig(false);

        // Step 2: Extract credentials and workspaceId
        const configData = orderFormConfig as any;
        const workspaceId = configData.workspaceId;

        // Prioritize credentials from config (for slug-based URLs), but allow overrides from search params (for old URLs)
        const clientId = searchParams.get("clientId") || configData.clientId;
        const clientSecret =
          searchParams.get("clientSecret") || configData.clientSecret;

        setWorkspaceId(workspaceId);

        if (!workspaceId || !clientId || !clientSecret) {
          setError(t("public_form.authentication.missing_credentials"));
          setIsAuthenticated(false);
          setIsAuthenticating(false);
          return;
        }

        // Step 3: Authenticate
        const params = {
          audience: workspaceId,
          client_id: clientId,
          client_secret: clientSecret,
          grant_type: "client_credentials",
        };

        const authResponse = await authenticatePublicAccess(params);
        setAccessToken(authResponse.access_token);
        setAuthParams(params);

        // Step 4: Set language based on config
        if (orderFormConfig?.language) {
          setIsLanguageChanging(true);
          await i18n.changeLanguage(orderFormConfig.language);
          await new Promise((resolve) => setTimeout(resolve, 100));
          setCurrentLanguage(orderFormConfig.language);
          setConfig({ ...orderFormConfig, _languageSet: true });
          setIsLanguageChanging(false);
        }

        // Step 5: Fetch products
        setIsLoadingProducts(true);
        const publicProducts = await fetchPublicProducts(
          workspaceId,
          authResponse.access_token,
        );
        setProducts(publicProducts);
        setIsLoadingProducts(false);

        // Step 6: Determine flow based on config
        const { allowCustomOrderFlow, allowProductListOrderFlow } =
          orderFormConfig as any;

        if (allowCustomOrderFlow && !allowProductListOrderFlow) {
          setSelectedFlow("custom-offer");
        } else if (!allowCustomOrderFlow && allowProductListOrderFlow) {
          setSelectedFlow("product-list");
        } else if (allowCustomOrderFlow && allowProductListOrderFlow) {
          setSelectedFlow(null);
        } else {
          setError(t("public_form.errors.no_flows_enabled"));
          setIsAuthenticated(false);
          setIsAuthenticating(false);
          return;
        }

        setIsAuthenticated(true);
      } catch (error: any) {
        console.error("Operation failed:", error);
        setError(
          error.response?.data?.message ||
            error.message ||
            t("public_form.authentication.failed"),
        );
        setIsAuthenticated(false);
      } finally {
        setIsAuthenticating(false);
        setIsLoadingConfig(false);
        setIsLoadingProducts(false);
      }
    };

    authenticate();
  }, [identifier, searchParams]);

  // Insert default pickup stop if configured and not yet added
  useEffect(() => {
    if (config?.defaultPickupLocation) {
      if (order.route.length === 0) {
        const pickupLocation = { ...config.defaultPickupLocation } as any;

        const pickupRouteItem: IRouteItem = {
          location: pickupLocation,
          stopNumber: 0,
          isPickup: true,
          stopDate: Timestamp.now(),
          timing: { earliest: true, latest: false },
        } as any;

        dispatch({
          type: "ADD_ROUTE",
          payload: { routeItem: pickupRouteItem },
        });
      }
    }
  }, [config, order.route.length]);

  // Effect to set language when config is loaded
  useEffect(() => {
    if (config?.language && i18n.language !== config.language) {
      i18n.changeLanguage(config.language).then(() => setLanguageReady(true));
    } else if (config?.language) {
      setLanguageReady(true);
    }
  }, [config, i18n.language]);

  const handleFlowSelect = (flow: OrderFlow) => {
    // Reset order state when switching flows
    dispatch({ type: "RESET_ORDER", payload: {} });
    setSelectedFlow(flow);
  };

  const handleBackToSelection = () => {
    // Reset order state when going back to flow selection
    dispatch({ type: "RESET_ORDER", payload: {} });
    setSelectedFlow(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated || !accessToken) {
      setError(t("public_form.authentication.missing_params"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Format the order route dates before submission
      const formattedOrder = {
        ...order,
        orderType:
          selectedFlow === "product-list" ? "PublicOrder" : "PriceRequest",
        route: order.route.map((routeItem) => ({
          ...routeItem,
          stopDate: routeItem.stopDate
            ? typeof routeItem.stopDate === "object" &&
              "toDate" in routeItem.stopDate
              ? routeItem.stopDate.toDate().toISOString().split("T")[0] // Convert Firebase Timestamp to YYYY-MM-DD
              : routeItem.stopDate // Keep as is if already formatted
            : null,
        })),
      };

      // For price requests, create dummy cargo to establish pickup/delivery relationship
      if (
        selectedFlow === "custom-offer" &&
        formattedOrder.cargo.length === 0
      ) {
        const pickupStops = formattedOrder.route.filter(
          (stop) => stop.isPickup === true,
        );
        const deliveryStops = formattedOrder.route.filter(
          (stop) => stop.isPickup === false,
        );

        if (pickupStops.length > 0 && deliveryStops.length > 0) {
          // Create dummy products to establish pickup/delivery relationships for each delivery stop
          const dummyProducts: any[] = [];

          deliveryStops.forEach((deliveryStop, index) => {
            const emptyProduct = new OrderProduct({
              ...OrderProduct.defaultProduct(),
              descriptionShort: `Prisforespørsel ${index + 1}`,
              description: order.comment || "",
              itinerary: {
                pickupStopNumber: pickupStops[0].stopNumber, // Use first pickup for all
                deliveryStopNumber: deliveryStop.stopNumber,
              },
            });
            dummyProducts.push(emptyProduct);
          });

          formattedOrder.cargo = dummyProducts;
        }
      }

      const response = await submitPublicOrder(
        formattedOrder as any,
        authParams,
        accessToken,
      );

      if (onSubmit) {
        onSubmit(response);
      }

      // Only show success page if submission was successful
      setSubmittedOrder(order);
      setIsSubmitted(true);
    } catch (error: any) {
      console.error("Error submitting order:", error);
      if (error.response?.status === 401) {
        setError(t("public_form.authentication.session_expired"));
        setIsAuthenticated(false);
        setAccessToken(null);
      } else {
        setError(
          error.response?.data?.message ||
            t("public_form.errors.submission_failed"),
        );
      }
      // Don't show success page on error - stay on the form
      setIsSubmitted(false);
      setSubmittedOrder(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (
    isAuthenticating ||
    isLoadingConfig ||
    isLoadingProducts ||
    isLanguageChanging
  ) {
    return (
      <div className="min-h-screen bg-gray-100 py-10 flex items-center justify-center">
        <div className="max-w-md w-full px-4">
          <div className="text-center">
            <div className="mx-auto mb-4">
              <BeatLoader color="#6b7280" size={15} />
            </div>
            <h2 className="mt-2 text-lg font-medium text-gray-900">
              {isAuthenticating
                ? t("public_form.loading.authenticating")
                : isLoadingConfig
                  ? t("public_form.loading.loading_config")
                  : isLoadingProducts
                    ? t("public_form.loading.loading_products")
                    : t("public_form.loading.changing_language")}
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              {isAuthenticating
                ? t("public_form.loading.authenticating_description")
                : isLoadingConfig
                  ? t("public_form.loading.loading_config_description")
                  : isLoadingProducts
                    ? t("public_form.loading.loading_products_description")
                    : t("public_form.loading.changing_language_description")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 py-10 flex items-center justify-center">
        <div className="max-w-md w-full px-4">
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <div className="text-center">
              <HiInformationCircle className="mx-auto h-12 w-12 text-red-500" />
              <h2 className="mt-2 text-lg font-medium text-gray-900">
                {t("public_form.authentication.required")}
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                {error || t("public_form.authentication.missing_params")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted && submittedOrder) {
    return (
      <PublicOrderSuccessPage
        key={`success-${currentLanguage}`}
        order={submittedOrder}
        config={config}
      />
    );
  }

  // Show flow selection if no flow has been selected yet
  if (!selectedFlow) {
    return (
      <PublicFlowSelection
        key={`flow-selection-${currentLanguage}`}
        onFlowSelect={handleFlowSelect}
        hasProducts={products.length > 0}
        config={config}
      />
    );
  }

  // Show the shared order form with appropriate settings based on selected flow
  if (!languageReady) {
    return null; // or a spinner if you prefer
  }

  return (
    <PublicOrderForm
      key={`order-form-${currentLanguage}`}
      order={order}
      dispatch={dispatch}
      products={products}
      isLoadingProducts={isLoadingProducts}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      error={error}
      allowProductSelection={selectedFlow === "product-list"}
      config={config}
      selectedFlow={selectedFlow}
      onBackToSelection={
        config?.allowCustomOrderFlow && config?.allowProductListOrderFlow
          ? handleBackToSelection
          : undefined
      }
      accessToken={accessToken || undefined}
      workspaceId={workspaceId || undefined}
    />
  );
};

export default PublicOrderPage;
